import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Plus, Percent } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SubscribeButton, periodText } from "@/components/SubscribeButton";

export default function Store() {
  const [tools, setTools] = useState([]);
  const { add, remove, has, count } = useCart();

  const { user } = useAuth();

  useEffect(() => {
    api.get("/catalog").then((r) => {
      const list = r.data.tools || [];
      setTools(list);
      list.filter((t) => t.is_subscription && has(t.id)).forEach((t) => remove(t.id));
    });
    // eslint-disable-next-line
  }, []);

  return (
    <div data-testid="store-page" className="min-h-[calc(100vh-4rem)] px-6 md:px-8 py-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-primary mb-2">// Store</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">HTML tools</h1>
            <p className="text-muted-foreground mt-2 text-sm">Monthly per-tool subscription · Discount auto-applied on 2+ tools</p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 border border-primary/30 bg-primary/5 rounded-sm">
            <Percent size={16} weight="bold" className="text-primary" />
            <span className="text-xs font-mono uppercase tracking-widest text-primary">2:5% · 3:10% · 4:15% · 5:20% · 6:25% · 10+:40%</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tools.map((t) => {
            const inCart = has(t.id);
            return (
              <div key={t.id} data-testid={`store-tool-${t.slug}`} className="border border-border p-6 rounded-sm bg-card flex flex-col">
                <div className="text-xs font-mono uppercase tracking-widest text-primary mb-2">{t.category}</div>
                <h3 className="font-display font-bold text-xl mb-2">{t.name}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-1">{t.description}</p>
                <div className="mb-4">
                  <span className="font-display font-black text-3xl">₹{t.price_inr}</span>
                  <span className="text-xs text-muted-foreground font-mono ml-1">/{t.is_subscription ? periodText(t) : "month"}</span>
                  {t.is_subscription && <div className="text-[11px] font-mono uppercase tracking-widest text-primary mt-1" data-testid={`autopay-tag-${t.slug}`}>Auto-debit · renews automatically</div>}
                </div>
                {t.is_subscription ? (
                  <SubscribeButton tool={t} owned={(user?.active_tool_ids || []).includes(t.id)} />
                ) : (
                <Button
                  onClick={() => { if (inCart) { remove(t.id); toast.info(`${t.name} removed`); } else { add(t.id); toast.success(`${t.name} added`); } }}
                  data-testid={`toggle-tool-${t.slug}`}
                  className={inCart ? "w-full rounded-sm font-bold bg-secondary hover:bg-secondary/80" : "w-full rounded-sm font-bold bg-primary text-black hover:bg-primary/90"}
                >
                  {inCart ? <><Check size={14} weight="bold" className="mr-2" /> In cart</> : <><Plus size={14} weight="bold" className="mr-2" /> Add to cart</>}
                </Button>
                )}
              </div>
            );
          })}
          {tools.length === 0 && <div className="col-span-full text-center text-muted-foreground py-16 border border-border rounded-sm">No tools yet.</div>}
        </div>

        {count > 0 && (
          <div className="mt-8 flex justify-end">
            <Link to="/cart"><Button data-testid="go-to-cart" size="lg" className="bg-primary text-black hover:bg-primary/90 font-bold rounded-sm">View cart ({count}) →</Button></Link>
          </div>
        )}
      </div>
    </div>
  );
}
